import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import { useAuth } from '../context/AuthContext';
import { adminLogin } from '../api/auth';
import logoMark from '../assets/logo-mark.png';

interface LocationState {
  from?: string;
}

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as LocationState | null)?.from ?? '/admin';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Enter both email and password.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await adminLogin({ email, password });
      login(res.token, res.user);
      navigate(from === '/admin/login' ? '/admin' : from, { replace: true });
    } catch (err: any) {
      // Backend shape: { status: 0, error: string | string[] }
      const data = err?.response?.data;
      const status = err?.response?.status;
      let msg: string;

      if (data?.error) {
        msg = Array.isArray(data.error) ? data.error.join(', ') : data.error;
      } else if (data?.message) {
        msg = data.message;
      } else if (status === 401) {
        msg = 'Invalid email or password.';
      } else if (status === 403) {
        msg = 'This account is pending approval.';
      } else if (err?.code === 'ERR_NETWORK') {
        msg = 'Cannot reach the server. Make sure the backend is running on port 3000.';
      } else if (
        err?.message &&
        !err.message.toLowerCase().includes('status code')
      ) {
        msg = err.message;
      } else {
        msg = 'Login failed. Check your credentials and try again.';
      }

      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-canvas">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img
            src={logoMark}
            alt="Rentagade"
            className="w-12 h-12 rounded-full object-contain flex-shrink-0"
          />
          <div className="text-left">
            <div className="text-xl font-extrabold text-ink tracking-tight">
              Rentagade
            </div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-g400">
              Admin Console
            </div>
          </div>
        </div>

        <div className="card p-8 animate-fadeIn">
          <h1 className="text-[22px] font-extrabold text-ink mb-1">Sign in</h1>
          <p className="text-sm text-g500 mb-6">
            Enter your admin credentials to continue.
          </p>

          {error && (
            <div
              role="alert"
              className="mb-4 rounded-[10px] px-4 py-3 text-sm font-medium flex items-start gap-2"
              style={{
                background: 'var(--red-bg)',
                color: 'var(--red)',
                border: '1px solid rgba(220,38,38,.12)',
              }}
            >
              <Icon name="x-circle" size={16} color="var(--red)" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-4">
              <label className="form-label" htmlFor="email">
                Email address
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-g400">
                  <Icon name="mail" size={16} color="var(--g400)" />
                </span>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="form-input"
                  style={{ paddingLeft: 38 }}
                  placeholder="admin@rentagade.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="mb-2">
              <label className="form-label" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-g400">
                  <Icon name="lock" size={16} color="var(--g400)" />
                </span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="form-input"
                  style={{ paddingLeft: 38, paddingRight: 42 }}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-md flex items-center justify-center hover:bg-g50"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon
                    name={showPassword ? 'eye-off' : 'eye'}
                    size={16}
                    color="var(--g500)"
                  />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between mb-6">
              <label className="flex items-center gap-2 text-sm text-g600 cursor-pointer">
                <input
                  type="checkbox"
                  style={{ accentColor: 'var(--brand)' }}
                  className="w-4 h-4"
                />
                Remember me
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary w-full justify-center"
              style={{ paddingTop: 12, paddingBottom: 12 }}
            >
              {submitting ? (
                'Signing in...'
              ) : (
                <>
                  Sign in <Icon name="chevron-right" size={16} color="#fff" />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-xs text-g500 text-center mt-6">
          Protected admin area. Unauthorized access is monitored.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
